import React, { useEffect, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import TableContainer from "./TableContainer";
import { useFilterState, useSortingState } from "../../hooks/useScrollPosition";
import { columns } from "./columnsConfig/positions";

const PositionsTable = ({
  data,
  title = "All Positions",
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "StrategyName", desc: false }],
  setSorting: setParentSorting,
  scrollPosition: initialScrollPosition = 0,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State Management
  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "positionsTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "positionsTableSorting",
    initialSorting,
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  const tableConfig = useMemo(
    () => ({
      enableColumnDragging: false,
      enableSorting: true,
      enableColumnOrdering: false,
      enablePagination: false,
      enableStickyHeader: true,
      enableStickyFooter: true, // Ensures footer sticks to the bottom
      enableColumnResizing: false,
      enableBottomToolbar: false,
      enablePinning: true,
      enableGlobalFilter: true,
      globalFilterFn: "contains",
      columnResizeMode: "onChange",
      enableTopToolbar: false,
      enableColumnActions: false,
      enableRowVirtualization: true,
      displayColumnDefOptions: {
        "mrt-row-numbers": {
          enableColumnOrdering: false,
          enableSorting: false,
        },
      },
      sortingDisplay: "none",
      muiTableBodyRowProps: {
        sx: {
          opacity: 1,
          backgroundColor: theme.palette.background.paper,
          padding: "0px",
          height: {
            xs: "30px", // Taller rows for mobile readability
            sm: "25px",
          },
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.grey[100],
          },
          "&:nth-of-type(odd)": {
            backgroundColor: theme.palette.grey[100],
          },
        },
      },
      muiTableFooterRowProps: {
        sx: {
          backgroundColor: theme.palette.primary.dark,
          fontWeight: 600,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased zIndex to ensure visibility
          borderTop: `2px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
          height: "40px", // Consistent footer height
        },
      },
      muiTableProps: {
        sx: {
          width: "100%", // Use full width instead of minWidth
          tableLayout: "auto", // Allow columns to size dynamically
          "& .MuiTableHead-root": {
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            height: "auto",
            minHeight: {
              xs: "40px", // Taller header for mobile
              sm: "35px",
            },
          },
          "& .MuiTableFooter-root": {
            borderTop: `2px solid ${theme.palette.divider}`,
            position: "sticky",
            bottom: 0,
            zIndex: 100, // Ensure footer stays above content
            backgroundColor: theme.palette.primary.dark,
          },
          "& .MuiTableFooter-root .MuiTableRow-root": {
            height: "40px",
          },
          "& .MuiTableFooter-root .MuiTableCell-root": {
            fontSize: {
              xs: "0.75rem",
              sm: "0.875rem",
            },
            color: theme.palette.primary.contrastText,
            padding: {
              xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
              sm: theme.spacing(0.75, 0.5, 0.75, 0.75),
            },
            boxSizing: "border-box",
          },
        },
      },
      initialState: {
        sorting: [
          { id: "StrategyType", desc: false }, // Fixed syntax error
          { id: "StrategyName", desc: false },
          { id: "SYMBOL", desc: false },
        ],
        density: "compact",
        showColumnFilters: false,
        showGlobalFilter: true,
        globalFilter: savedGlobalFilter,
        columnPinning: isDesktop
          ? {
              left: ["StrategyType", "StrategyName", "SYMBOL", "security_name"],
            }
          : {}, // No columns pinned in mobile mode
      },
      state: {
        globalFilter: savedGlobalFilter,
        sorting: sorting,
      },
      onGlobalFilterChange: setSavedGlobalFilter,
      onSortingChange: setSorting,
    }),
    [theme, isDesktop, savedGlobalFilter, sorting],
  );

  // Render
  return (
    <TableContainer
      title={title}
      data={data}
      columnsConfig={columns}
      globalFilter={savedGlobalFilter}
      setGlobalFilter={setSavedGlobalFilter}
      sorting={sorting}
      setSorting={setSorting}
      scrollPosition={null}
      tableConfig={tableConfig}
    />
  );
};

export default PositionsTable;
